import axios from 'axios';
import { Capacitor } from '@capacitor/core';

// Auditoria dos uploads: cada clique em "Adicionar" é um envio. Junta-se aqui o que o browser vê
// (ficheiros, compressão, tempos, dispositivo) e no fim manda-se o resumo ao BFF, que acrescenta o utilizador da sessão.
// O BFF regista à parte cada pedido de upload (lote) com os tempos do servidor, ligado pelo cabeçalho X-Envio-Id.

const novoEnvioId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

let dispositivoEmCache = null;

// Modelo e versão do sistema: o Chrome Android esconde o modelo no user agent ("K"), mas dá-o por client hints
export async function obterDispositivo() {
    if (dispositivoEmCache) {
        return dispositivoEmCache;
    }
    const ua = navigator.userAgent || '';
    let hints = {};
    try {
        if (navigator.userAgentData?.getHighEntropyValues) {
            hints = await navigator.userAgentData.getHighEntropyValues(['model', 'platformVersion']);
        }
    } catch (error) {
        hints = {};
    }

    const android = ua.match(/Android ([\d.]+)(?:; ([^;)]+))?/);
    const ios = ua.match(/(iPhone|iPad).*? OS (\d+[_\d]*)/);
    let sistema = 'Desktop';
    let modelo = hints.model || '';
    if (android) {
        // O Chrome reduzido diz sempre "Android 10"; a versão real vem nos client hints
        sistema = `Android ${hints.platformVersion ? hints.platformVersion.split('.')[0] : android[1]}`;
        if (!modelo && android[2] && android[2].trim() !== 'K') {
            modelo = android[2].replace(/ Build\/.*$/, '').trim();
        }
    } else if (ios) {
        sistema = `iOS ${ios[2].replace(/_/g, '.')}`;
        modelo = ios[1];
    } else if (/Macintosh/.test(ua)) {
        sistema = 'macOS';
    } else if (/Windows/.test(ua)) {
        sistema = 'Windows';
    }

    const plataforma = Capacitor.getPlatform();
    const browser = plataforma !== 'web'
        ? `App ${plataforma === 'ios' ? 'iOS' : 'Android'}`
        : /EdgA?\//.test(ua) ? 'Edge'
            : /SamsungBrowser/.test(ua) ? 'Samsung Internet'
                : /CriOS|Chrome\//.test(ua) ? 'Chrome'
                    : /FxiOS|Firefox\//.test(ua) ? 'Firefox'
                        : /Safari\//.test(ua) ? 'Safari' : 'Browser';

    dispositivoEmCache = {
        resumo: [browser, sistema, modelo].filter(Boolean).join(' · '),
        browser,
        sistema,
        modelo,
        appNativa: plataforma !== 'web',
        userAgent: ua,
        ecra: `${window.screen?.width}x${window.screen?.height} @${window.devicePixelRatio || 1}x`,
        memoriaGb: navigator.deviceMemory,
    };
    return dispositivoEmCache;
}

const estadoDaRede = () => {
    const ligacao = navigator.connection;
    return {
        online: navigator.onLine,
        tipo: ligacao?.effectiveType,
        downlinkMbps: ligacao?.downlink,
        rttMs: ligacao?.rtt,
    };
};

export function iniciarEnvio(tipo, reservaId, ficheiros) {
    const envio = {
        envio_id: novoEnvioId(),
        tipo,
        reserva_id: reservaId,
        inicio: new Date().toISOString(),
        t0: performance.now(),
        dispositivoPromise: obterDispositivo(),
        ficheiros: Array.from(ficheiros).map(f => ({ nome: f.name, tipo: f.type, bytesOriginal: f.size })),
        lotes: [],
    };
    envio.redeInicio = estadoDaRede();
    return envio;
}

// Cabeçalhos que ligam o pedido de upload ao envio, no registo que o BFF faz do lote
export const cabecalhosAuditoria = (envio, lote) => ({ 'X-Envio-Id': envio.envio_id, 'X-Lote': String(lote) });

export function registarLote(envio, lote) {
    envio.lotes.push(lote);
}

export async function terminarEnvio(envio, erro) {
    try {
        const dispositivo = await envio.dispositivoPromise;
        const lotesOk = envio.lotes.filter(l => l.status >= 200 && l.status < 300);
        const nEnviados = lotesOk.reduce((total, l) => total + l.nFicheiros, 0);
        const estado = erro ? (nEnviados > 0 ? 'parcial' : 'falhou') : 'ok';
        await axios.post('/proxy-media/auditoria/envio', {
            envio_id: envio.envio_id,
            tipo: envio.tipo,
            reserva_id: envio.reserva_id,
            estado,
            n_ficheiros: envio.ficheiros.length,
            n_enviados: nEnviados,
            bytes_original: envio.ficheiros.reduce((total, f) => total + (f.bytesOriginal || 0), 0),
            bytes_enviados: lotesOk.reduce((total, l) => total + l.bytes, 0),
            ms_total: Math.round(performance.now() - envio.t0),
            dispositivo: dispositivo.resumo,
            erro: erro ? (erro.response ? `HTTP ${erro.response.status}: ${erro.response.data?.message || erro.message}` : erro.message) : '',
            detalhes: {
                inicio: envio.inicio,
                fim: new Date().toISOString(),
                dispositivo,
                redeInicio: envio.redeInicio,
                redeFim: estadoDaRede(),
                ficheiros: envio.ficheiros,
                lotes: envio.lotes,
            },
        });
    } catch (error) {
        // A auditoria nunca pode afectar o upload
        console.error('Auditoria do upload:', error.message);
    }
}
